import { useState, useMemo } from 'react';
import { X, ChevronDown, Settings2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { CompanyGroupFilters } from '@/components/layout/CompanyGroupFilters';
import { TimePeriodSelector } from './TimePeriodSelector';
import type { CompareState, CompareSlot, CompareSection, DatePreset, TimeGranularity } from '@/types/reports';
import { getDateRangeFromPreset } from '@/types/reports';
import type { Group, Company, IndustryType } from '@/types';

// Section content components
import {
  CompareInfoSection,
  CompareKPIsSection,
  CompareChartSection,
  CompareStatsSection,
  CompareEnergyMixSection,
  CompareGridRevenueSection,
} from './sections';

interface CompareSlotCardProps {
  slot: CompareSlot;
  index: number;
  activeSections: CompareSection[];
  activeSection?: CompareSection; // Used in compact mode to render specific section
  onRemove: () => void;
  onSlotChange: (updates: Partial<CompareSlot>) => void;
  groups: Group[];
  companies: Company[];
  selectedIndustryTypes: IndustryType[];
  groupLabel: string;
  compareState: CompareState;
  className?: string;
  /** When true, renders just the section content without card wrapper and header */
  compactMode?: boolean;
}

export function CompareSlotCard({
  slot,
  index: _index,
  activeSections,
  activeSection,
  onRemove,
  onSlotChange,
  groups,
  companies: _companies,
  selectedIndustryTypes,
  groupLabel,
  compareState,
  className,
  compactMode = false,
}: CompareSlotCardProps) {
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const { linkedDateRange, linkedGranularity, globalDatePreset, globalCustomDateRange, globalGranularity } = compareState;

  // Helper to parse ISO date string as local date (avoids timezone issues)
  const parseLocalDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Find the root/parent groups for useFullGroup mode (ones with dates)
  // When multiple productions are selected, we need all root groups
  const rootGroups = useMemo(() => {
    if (slot.groupIds.length === 0) return [];

    // Get all selected groups
    const selectedGroups = slot.groupIds
      .map(id => groups.find(g => g.id === id))
      .filter((g): g is Group => !!g);

    if (selectedGroups.length === 0) return [];

    // Find root groups (ones without a parent in selection, or no parent at all)
    const selectedSet = new Set(slot.groupIds);
    const roots = selectedGroups.filter(g => !g.parent_group_id || !selectedSet.has(g.parent_group_id));

    return roots.length > 0 ? roots : [selectedGroups[0]];
  }, [slot.groupIds, groups]);

  // Get effective date range for this slot
  const effectiveDateRange = useMemo(() => {
    // If useFullGroup is true, calculate combined date range from all root groups
    if (slot.useFullGroup && rootGroups.length > 0) {
      let earliestStart: Date | null = null;
      let latestEnd: Date | null = null;

      rootGroups.forEach(g => {
        if (g.start_date) {
          const start = parseLocalDate(g.start_date);
          if (!earliestStart || start < earliestStart) earliestStart = start;
        }
        if (g.end_date) {
          const end = parseLocalDate(g.end_date);
          if (!latestEnd || end > latestEnd) latestEnd = end;
        }
      });

      // Only use group dates if we have both start and end
      if (earliestStart && latestEnd) {
        return { start: earliestStart, end: latestEnd };
      }
    }

    // If linked, use global settings
    if (linkedDateRange) {
      if (globalDatePreset === 'custom' && globalCustomDateRange.start && globalCustomDateRange.end) {
        return { start: globalCustomDateRange.start, end: globalCustomDateRange.end };
      }
      return getDateRangeFromPreset(globalDatePreset);
    }
    // Otherwise use slot-specific settings
    if (slot.dateRangeMode === 'custom' && slot.customDateRange?.start && slot.customDateRange?.end) {
      return { start: slot.customDateRange.start, end: slot.customDateRange.end };
    }
    if (slot.datePreset) {
      return getDateRangeFromPreset(slot.datePreset);
    }
    return getDateRangeFromPreset('last-30-days');
  }, [linkedDateRange, globalDatePreset, globalCustomDateRange, slot, rootGroups]);

  // Get effective granularity for this slot
  const effectiveGranularity = useMemo(() => {
    if (linkedGranularity || linkedDateRange) {
      return globalGranularity;
    }
    return slot.granularity;
  }, [linkedGranularity, linkedDateRange, globalGranularity, slot.granularity]);

  // Get slot label - smart labeling that recognizes parent+children selections
  const slotLabel = useMemo(() => {
    if (slot.label) return slot.label;
    if (slot.groupIds.length === 0) return `Select ${groupLabel}`;
    if (slot.groupIds.length === 1) {
      const group = groups.find(g => g.id === slot.groupIds[0]);
      return group?.name || 'Unknown';
    }

    // Multiple groups selected - find root groups (parents not in selection)
    const selectedSet = new Set(slot.groupIds);
    const rootGroups = slot.groupIds
      .map(id => groups.find(g => g.id === id))
      .filter((g): g is Group => !!g)
      .filter(g => !g.parent_group_id || !selectedSet.has(g.parent_group_id));

    // If exactly one root group is selected (with its children), show that group's name
    if (rootGroups.length === 1) {
      return rootGroups[0].name;
    }

    // If exactly two root groups, show both names
    if (rootGroups.length === 2) {
      return `${rootGroups[0].name} & ${rootGroups[1].name}`;
    }

    // Otherwise, count root groups only
    return `${rootGroups.length} ${groupLabel}s`;
  }, [slot, groups, groupLabel]);

  // Get structured group data for tooltip (when 2+ productions selected)
  const groupsForTooltip = useMemo(() => {
    if (slot.groupIds.length < 2) return null;

    // Find root groups (parents not in selection)
    const selectedSet = new Set(slot.groupIds);
    const rootGroups = slot.groupIds
      .map(id => groups.find(g => g.id === id))
      .filter((g): g is Group => !!g)
      .filter(g => !g.parent_group_id || !selectedSet.has(g.parent_group_id));

    // For each root group, get its children that are selected
    return rootGroups.map(root => {
      const children = groups
        .filter(g => g.parent_group_id === root.id && selectedSet.has(g.id))
        .map(g => g.name);
      return {
        name: root.name,
        children,
      };
    });
  }, [slot.groupIds, groups]);

  // Handle group selection - smart labeling that recognizes parent+children selections
  const handleGroupsChange = (groupIds: string[]) => {
    let label = '';
    if (groupIds.length === 1) {
      const group = groups.find(g => g.id === groupIds[0]);
      label = group?.name || '';
    } else if (groupIds.length > 1) {
      // Multiple groups selected - find root groups (parents not in selection)
      const selectedSet = new Set(groupIds);
      const rootGroups = groupIds
        .map(id => groups.find(g => g.id === id))
        .filter((g): g is Group => !!g)
        .filter(g => !g.parent_group_id || !selectedSet.has(g.parent_group_id));

      // If exactly one root group is selected (with its children), use that group's name
      if (rootGroups.length === 1) {
        label = rootGroups[0].name;
      } else {
        // Otherwise, count root groups only
        label = `${rootGroups.length} ${groupLabel}s`;
      }
    }

    onSlotChange({
      groupIds,
      label,
      type: groupIds.length > 1 ? 'group' : 'individual',
    });
    setSelectorOpen(false);
  };

  // Handle date preset change (for per-slot mode)
  const handleDatePresetChange = (preset: DatePreset) => {
    onSlotChange({
      datePreset: preset,
      dateRangeMode: 'preset',
      customDateRange: { start: null, end: null },
    });
  };

  // Handle custom date range change (for per-slot mode)
  const handleCustomDateRangeChange = (start: Date | null, end: Date | null) => {
    onSlotChange({
      datePreset: 'custom',
      dateRangeMode: 'custom',
      customDateRange: { start, end },
    });
  };

  // Handle granularity change (for per-slot mode)
  const handleGranularityChange = (granularity: TimeGranularity) => {
    onSlotChange({ granularity });
  };

  // Render the active section content
  // In compact mode, use the specific activeSection prop
  // In full card mode, render all active sections
  const renderSectionContent = (section: CompareSection) => {
    const commonProps = {
      slot,
      dateRange: effectiveDateRange,
      granularity: effectiveGranularity,
      groups,
      selectedIndustryTypes,
    };

    switch (section) {
      case 'info':
        return <CompareInfoSection {...commonProps} />;
      case 'kpis':
        return <CompareKPIsSection {...commonProps} />;
      case 'chart':
        return <CompareChartSection {...commonProps} enabledMetrics={compareState.enabledMetrics} />;
      case 'stats':
        return <CompareStatsSection {...commonProps} />;
      case 'energyMix':
        return <CompareEnergyMixSection {...commonProps} />;
      case 'gridRevenue':
        return <CompareGridRevenueSection {...commonProps} />;
      default:
        return null;
    }
  };

  // Compact mode: just render the section content without wrapper
  if (compactMode && activeSection) {
    return (
      <div className={cn(
        "flex flex-col",
        activeSection === 'chart' && "min-h-[300px]",
        activeSection === 'energyMix' && "min-h-[250px]",
        activeSection === 'info' && "min-h-[200px]",
        !['chart', 'energyMix', 'info'].includes(activeSection) && "min-h-[180px]"
      )}>
        {slot.groupIds.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-center text-sm text-muted-foreground">
            No {groupLabel.toLowerCase()} selected
          </div>
        ) : (
          <div className="flex-1">
            {renderSectionContent(activeSection)}
          </div>
        )}
      </div>
    );
  }

  // Full card mode with header
  return (
    <div
      className={cn(
        "bg-card border rounded-lg overflow-hidden",
        className
      )}
      style={{ borderColor: slot.color, borderLeftWidth: '4px' }}
    >
      {/* Card Header */}
      <div className="flex items-center justify-between p-3 border-b border-border bg-muted/20">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {/* Group Selector Popover with Hover Tooltip for multiple selections */}
          {groupsForTooltip ? (
            <TooltipProvider>
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild>
                  <div className="flex-1 min-w-0">
                    <Popover open={selectorOpen} onOpenChange={setSelectorOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={cn(
                            "h-7 px-2 text-sm font-medium truncate max-w-[200px] w-full justify-between",
                            slot.groupIds.length === 0 && "text-muted-foreground"
                          )}
                        >
                          <span className="truncate">{slotLabel}</span>
                          <ChevronDown className="h-3.5 w-3.5 ml-1 flex-shrink-0" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-72 p-2" align="start">
                        <CompanyGroupFilters
                          selectedGroupIds={slot.groupIds}
                          onGroupsChange={handleGroupsChange}
                          selectedIndustryTypes={selectedIndustryTypes}
                          embeddedMode
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  <div className="space-y-2">
                    <h4 className="text-xs font-medium uppercase tracking-wider">Selected {groupLabel}s</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {groupsForTooltip.map((group, idx) => (
                        <div key={idx} className="space-y-1">
                          <div className="text-sm font-medium">{group.name}</div>
                          {group.children.length > 0 && (
                            <ul className="text-xs text-muted-foreground space-y-0.5 pl-3">
                              {group.children.map((child, childIdx) => (
                                <li key={childIdx} className="list-disc">{child}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <Popover open={selectorOpen} onOpenChange={setSelectorOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "h-7 px-2 text-sm font-medium truncate max-w-[200px]",
                    slot.groupIds.length === 0 && "text-muted-foreground"
                  )}
                >
                  <span className="truncate">{slotLabel}</span>
                  <ChevronDown className="h-3.5 w-3.5 ml-1 flex-shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0" align="start">
                <div className="p-2 max-h-[350px] overflow-y-auto">
                  <CompanyGroupFilters
                    selectedGroupIds={slot.groupIds}
                    onGroupsChange={handleGroupsChange}
                    selectedIndustryTypes={selectedIndustryTypes}
                    embeddedMode
                  />
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>

        <div className="flex items-center gap-1">
          {/* Per-slot settings (only shown when not linked) */}
          {!linkedDateRange && (
            <Popover open={settingsOpen} onOpenChange={setSettingsOpen}>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7">
                  <Settings2 className="h-3.5 w-3.5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-72" align="end">
                <div className="space-y-3">
                  <h4 className="text-sm font-medium">Date & Granularity</h4>
                  <TimePeriodSelector
                    granularity={slot.granularity}
                    periodCount={4}
                    useFullGroup={slot.useFullGroup}
                    onGranularityChange={handleGranularityChange}
                    onPeriodCountChange={() => {}}
                    onUseFullProductionChange={(useFullGroup) => onSlotChange({ useFullGroup })}
                    datePreset={slot.datePreset || 'last-30-days'}
                    customStartDate={slot.customDateRange?.start}
                    customEndDate={slot.customDateRange?.end}
                    onDatePresetChange={handleDatePresetChange}
                    onCustomDateRangeChange={handleCustomDateRangeChange}
                    groupLabel={groupLabel}
                  />
                </div>
              </PopoverContent>
            </Popover>
          )}

          {/* Remove button */}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-destructive"
            onClick={onRemove}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Section Content - render all active sections stacked */}
      <div className="p-3 min-h-[200px] space-y-4">
        {slot.groupIds.length === 0 ? (
          <div className="h-full flex items-center justify-center text-center">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Select a {groupLabel.toLowerCase()} to view data
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectorOpen(true)}
              >
                Select {groupLabel}
              </Button>
            </div>
          </div>
        ) : (
          activeSections.map((section) => (
            <div key={section}>
              {renderSectionContent(section)}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
